import { DialogActions, DialogContent, DialogTitle, Grid, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  updateStatus,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
  ClientStatusDTO,
  ClientDTO,
  getClientStatusListByVisibility,
} from 'wisestep-api';
import { Button, ControlledTextInput, WsSelect, WsSelectObj, ILocation } from 'wisestep-base';

import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

const ClientStatusUpdateForm = ({
  onSave,
  onCancel,
  clientID,
  clientName,
}: {
  onSave: (client: ClientDTO) => void;
  onCancel: () => void;
  defaultValues?: ClientStatusDTO;
  options?: WsSelectObj[];
  placementID: string;
  clientID: string;
  clientName?: string;
  currencyId: string;
  ourEntityId: string;
  submissionId: string;
  feeArrangementId: string;
  candidateLocation: ILocation;
  clientRecruiterIdOne: string;
  clientRecruiterIdTwo: string;
  candidateId: string;
}) => {
  const form = useForm();
  const {
    formState: { dirtyFields },
  } = form;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isLoading, setIsLoading] = useState(false);
  const [ourClientStatusOptions, setOurClientStatusOptions] = useState<WsSelectObj[]>([]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      data.clientStatus = { id: data.clientStatusId };
      const response = await updateStatus(clientID, data);
      const payload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFields = Object.keys(dirtyFields).toString().split(',');
      payload.action = LogAction.Edit;
      payload.description = `updated ${avChangedValueViaDirtyFields} in update client status for`;
      payload.parentEntitiyType = LogContextType.Client;
      payload.entityType = LogEntityType.Candidate;
      payload.entityId = data.clientStatusId;
      payload.parentEntitiyId = clientID;
      payload.field = `Client Update Client Status`;
      payload.entityName = 'Client Status';
      payload.contextData = {
        clientID: clientID,
        field: `Client Update Client Status`,
      };
      avChangedValueViaDirtyFields[0].length > 0 ? await saveActivityLog(payload) : '';

      setNotification(success('Client status has been updated successfully !!'));
      onSave(response.data);
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setIsLoading(false);
  };

  const fetchClientStatusList = useCallback(async () => {
    try {
      const response = await getClientStatusListByVisibility();
      const optionsArray: WsSelectObj[] = response.data.map((obj: ClientStatusDTO) => ({
        label: obj.clientStatusName || '',
        value: obj.id || '',
      }));
      setOurClientStatusOptions(optionsArray);
    } catch (error) {
      console.error('Failed to fetch client status list:', error);
    }
  }, []);

  useEffect(() => {
    fetchClientStatusList();
  }, [fetchClientStatusList]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>Update Client Status for {clientName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <WsSelect
                name="clientStatusId"
                selectArray={ourClientStatusOptions}
                placeholder="Select Client Status"
                required
              />
            </Grid>
            <Grid item xs={10}>
              <ControlledTextInput
                name="clientStatusReason"
                labelText="Provide Reason for Client Status"
                size="medium"
                fullWidth={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="submit"
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default ClientStatusUpdateForm;
